import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Dropdown, SpreadsheetUtils, WithTooltip } from 'basic-components';
import { Info } from 'basic-components/icons';
import TimeService from 'dashboard-services/services/TimeService';
import { DateFormatPicker } from 'primary-components';

import { getHeaderChanges, getRowsChanges, onFail } from '../CSVService';
import MessageContainer from '../MessageContainer';

import dayjs from 'dayjs';

const DATE_COLUMN = "Date"
const DateStep = ({ setErrorCells, setInitHeader, setIsError, setIsSucess, worksheetRef, ref }) => {
  const [column, setColumn] = useState(),
        [dateFormat, setDateFormat] = useState(),
        [timezone, setTimezone] = useState(),
        [isValid, setIsValid] = useState(false),
        [initDateArray, setInitDateArray] = useState([]),
        [errorMessage, setErrorMessage] = useState(undefined),
        [successMessage, setSuccessMessage] = useState(undefined),
        turnOnError = useCallback(() => {
          setIsValid(false)
          setIsSucess(false)
          setIsError(true)
        }, [setIsSucess, setIsError]),        
        getRows = useCallback(() => worksheetRef.current.getRows(), [worksheetRef]),
        getColumns = useCallback(() => worksheetRef.current?.getColumns(), [worksheetRef]),
        rows = useMemo(() => getRows(),[getRows]),
        [columns, setColumns] = useState(() => getColumns().map(c => c.columnName)),
        columnRef = useRef(),
        dateFormatRef = useRef(),
        timezoneRef = useRef(),
        formatDate = useCallback(({ column, dateFormat, timezone, initDateArray }) => formatDateColumn({ getColumns, getRows, column, columnRef, dateFormat, timezone, worksheetRef, setIsSucess, setIsError, setInitHeader, setErrorCells, setColumns, setColumn, setDateFormat, setTimezone, setErrorMessage, setSuccessMessage, initDateArray }), [getColumns, getRows, setErrorCells, setInitHeader, setIsError, setIsSucess, worksheetRef]),
        onChange = useCallback(({ column, dateFormat, timezone }) => {
          const colIndex = columns.indexOf(column)
          if(initDateArray.length === 0){
            setInitDateArray(rows.filter(row => !String(row.rowId).startsWith("header")).map(row => row.cells[colIndex]?.text))
          }
          if(column && dateFormat && timezone && initDateArray.length > 0) {
            formatDate({ column, dateFormat, timezone, initDateArray })
          }
        }, [columns, formatDate, initDateArray, rows]),
        onColumnChange = useCallback(value => {
          setColumn(value)
          onChange({ column: value, dateFormat, timezone })
        }, [dateFormat, onChange, timezone]),
        onDateFormatChange = useCallback(value => {
          setDateFormat(value)
          onChange({ column, dateFormat: value, timezone })
        }, [column, onChange, timezone]),
        onTimezoneChange = useCallback(value => {
          setTimezone(value)
          onChange({ column, dateFormat, timezone: value })
        }, [column, onChange, dateFormat])

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => validate({ getColumns, turnOnError, getRows, setErrorCells, setIsSucess, setIsValid, setIsError, setErrorMessage, setSuccessMessage }),
    isValid: () => isValid,
    setColumns
  }))

  return (
    <div className="ncsv-creator__top__step ncsv-creator__top__step--date-time">
      <div className="ncsv-creator__top__step__row ncsv-creator__top__step__row--label">
        <div className="ncsv-creator__top__step__row__label__text">Select date column</div>
        <WithTooltip
            data-place="top"
            text="Column that contains date or datetime.
            Timestamp along with the symbol columns will create a unique identifier of the values in the system's database."
        >
          <div className="ncsv-creator__top__step__row__label__info">
            <Info
                color="blue-bright"
                height={16}
                width={16}
            />
          </div>
        </WithTooltip>
      </div>
      <div className="ncsv-creator__top__step__row ncsv-creator__top__step__row--value">
        <Dropdown
            fixed
            name="dateColumn"
            onChange={onColumnChange}
            options={columns}
            placeholder="Select a column"
            ref={columnRef}
        />
      </div>
      <div className="ncsv-creator__top__step__row ncsv-creator__top__step__row--value">
        <DateFormatPicker
            dateFormat={dateFormat}
            fixed
            name="dateFormat"
            onChange={onDateFormatChange}
            placeholder="File date format"
            ref={dateFormatRef}
        />
        <Dropdown
            fixed
            name="timezone"
            noError
            onChange={onTimezoneChange}
            options={TimeService.TIME_ZONES}
            placeholder="File timezone"
            ref={timezoneRef}
        />
      </div>
      <MessageContainer
          errorMessage={errorMessage}
          successMessage={successMessage}
      />
    </div>
  )
}

DateStep.displayName = "DateStep"

DateStep.propTypes = {
  setErrorCells: PropTypes.func.isRequired,
  setInitHeader: PropTypes.func.isRequired,
  setIsError: PropTypes.func.isRequired,
  setIsSucess: PropTypes.func.isRequired,
  worksheetRef: PropTypes.object.isRequired,
}

export default DateStep

const onSucces = ({ worksheetRef, headerChanges, rowChanges, setIsSucess, setIsError, column, columns, setErrorMessage, setSuccessMessage }) => {
  worksheetRef.current?.handleChanges([
    ...headerChanges,
    ...rowChanges,
    SpreadsheetUtils.getColumnReorderChange({ columnIds: [column], targetColumnId: columns[0]?.columnId , columns })
  ])
  setErrorMessage()
  setSuccessMessage("Date column was renamed and formatted succesfully.")
  setIsSucess(true)
  setIsError(false)
}

const isValid = ({ currDateIndex, colIndex, column, setErrorMessage, setSuccessMessage }) => {
  if(currDateIndex > -1 && currDateIndex !== colIndex && colIndex !== -1) {
    setErrorMessage("Date columns already exist")
    setSuccessMessage()
    return false
  }
  else if(colIndex === -1) {
    setErrorMessage(`Could not find column ${column}. Please try again.`)
    setSuccessMessage()
    return false
  }
  return true
}

const refreshDropdown = ({ setColumns, columns, colIndex, setColumn }) => {
  setColumn(DATE_COLUMN)
  setColumns(stateColumns => {
    if(stateColumns.findIndex(sc => sc === DATE_COLUMN) === -1){
      return columns.map(c => c?.columnId).remove(colIndex).add(DATE_COLUMN, 0)
    } else {
      return stateColumns
    }
})
}

const formatDateColumn = ({ getColumns, getRows, column, columnRef, dateFormat, timezone, worksheetRef, setIsSucess, setIsError, setInitHeader, setErrorCells, setColumns, setColumn, setErrorMessage, setSuccessMessage, initDateArray }) => {
  const successRows = [], 
        failCells = [],
        columns = getColumns(),
        rows = getRows(),
        colIndex = columns.findIndex(c => c.columnName === column),
        currDateIndex = columns.findIndex(c => c.columnName === DATE_COLUMN)
  if(!isValid({ currDateIndex, colIndex, column, setErrorMessage, setSuccessMessage })) {
    return;
  }
  const rowChanges = getRowsChanges({ rows, colIndex, dateFormat, timezone, successRows, failCells, columnId: columns[colIndex]?.columnId, initDateArray })
  const headerChanges = getHeaderChanges({ rows, colIndex, columnId: columns[colIndex]?.columnId, newValue: DATE_COLUMN })
  if(failCells?.length === 0 ) {
    onSucces({ worksheetRef, headerChanges, rowChanges, setIsSucess, setIsError, column, columns, setErrorMessage, setSuccessMessage })
    refreshDropdown({ setColumns, columns, colIndex, columnRef, setColumn })
  } else {
    onFail({ successRows, setIsSucess, setIsError, failCells, setSuccessMessage, setErrorMessage })
  }
  setInitHeader(columns.map(c => c?.columnId))
  setErrorCells(failCells)
}

const validate = ({ getColumns, turnOnError, getRows, setErrorCells, setIsSucess, setIsValid, setIsError, setErrorMessage, setSuccessMessage }) => {
  const columns = getColumns(),
        currDateIndex = columns.findIndex(c => c.columnName === DATE_COLUMN)
  if (currDateIndex === -1) {
    turnOnError()
    setSuccessMessage()
    setErrorMessage("Date column does not exist.")
    return false;
  } else if (columns.filter(c => c?.columnId === DATE_COLUMN).length > 1) {
    turnOnError()
    setSuccessMessage()
    setErrorMessage("There is more than one Date column. Verify the table headers.")
    return false;
  } else {
    return validateDateFormat({ getRows, currDateIndex, setErrorCells, turnOnError, setIsSucess, setIsValid, setIsError, setErrorMessage, setSuccessMessage })
  }
}
const validateDateFormat = ({ getRows, currDateIndex, setErrorCells, turnOnError, setIsSucess, setIsValid, setIsError, setErrorMessage, setSuccessMessage }) => {
  const rows = getRows(), failCells = []
  rows.filter(row => !String(row.rowId).startsWith("header")).forEach(row => {
    try {
      dayjs.tz(row.cells[currDateIndex]?.text, "YYYY-MM-DDTHH:mm:ss", "UTC")
    } catch {
      failCells.push({ columnNumber: currDateIndex, rowNumber: row?.rowId, message: "There was a problem while formatting your date. Please change the date format." })
    }
  })
  if (failCells.length > 0) {
    onValidateFail({ failCells, setErrorCells, turnOnError, setErrorMessage, setSuccessMessage })
    return false;
  } else {
    onValidateSuccess({ setErrorCells, setIsSucess, setIsValid, setIsError, setErrorMessage })
    return true;
  }
}

const onValidateFail = ({ failCells, setErrorCells, turnOnError, setErrorMessage, setSuccessMessage }) => {
  setSuccessMessage()
  setErrorMessage(`Some rows does not match proper date format. Please verify the data. Failed rows: ${failCells.map(cell => cell?.rowNumber + 1)}`)
  setErrorCells(failCells)
  turnOnError()
}

const onValidateSuccess = ({ setErrorCells, setIsSucess, setIsValid, setIsError, setErrorMessage }) => {
  setErrorMessage()
  setErrorCells([])
  setIsSucess(true)
  setIsValid(true)
  setIsError(false)
}